// libraries
import type { Reducer } from 'redux';

// utils
import { ROLL_TO_TARGET_FIELD_MAP } from '../constants';

// consts/enums
import {
    APPLY_ROLL_TO_OPTIONS,
    APPLY_ROLL_TO_OPTIONS_SUCCESS,
    APPLY_ROLL_TO_OPTIONS_FAILURE,
    CLEAR_ROLL_TO_OPTIONS,
    REQUEST_ROLL_TO_OPTIONS,
    REQUEST_ROLL_TO_PAYMENTS,
    REQUEST_ROLL_TO_PAYMENTS_FAILURE,
    REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPQ,
    REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR,
    RESET_ROLL_TO_OPTIONS,
    REVERT_ROLL_TO_ORIGINAL,
    ROLL_TO_PAYMENT_TOGGLE,
    SET_UNSAVED_CHANGES,
    UPDATE_ADJUST_ROLL_TO_CHANGES,
    UPDATE_ROLL_ADJUST,
    UPDATE_ROLL_TARGET_PAYMENT,
    UPDATE_ROLL_TO_OPTIONS,
    CLEAR_ROLL_TO_ERRORS,
    SET_ROLL_TO_ACTIVE_TAB,
    UPDATE_ROLL_TARGET_BALANCE_DUE,
    UPDATE_ROLL_TARGET_CASH_DUE,
    UPDATE_ROLL_TARGET_PROFIT
} from '../actionTypes/rollToOptionsActionTypes';

import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';

// types
import type {
    RequestRollToPaymentsPayload,
    RollProgramItem,
    RollToActionValues,
    RollToActions,
    RollToErrors,
    RollToOptionsState,
    RollToTabState,
    RollValueToMap,
    TargetStatus
} from '@makemydeal/dr-dash-types';
import type { PaymentProfitDetails } from '@makemydeal/dr-platform-types';

// util
import { parseErrorMessages } from '../utils/psErrorUtils';

import { pick } from 'lodash';
import { ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';

export type RequestRollToOptionsPayload = {
    rollToItems: RollToActions;
    balanceDue?: number;
    cashDue?: number;
    payment?: number;
};

export type UpdateRollToOptionsPayload = { rollToItems: RollToActions; balanceDue?: number; cashDue?: number };

export type RequestRollToPaymentsFailurePayload = {
    response?: {
        errors?: RollToErrors;
        message?: string;
    };
};

export type RequestRollToPaymentUPQPayload = {
    profit: PaymentProfitDetails | undefined;
    actionValue: number | undefined;
    totalTaxes: number | 0;
    balanceDue: number | 0;
    cashDue?: number;
    totalFees: number | 0;
    VppAndProtectionTotal: number | 0;
    payment: number | 0;
};

export type RollToOptionsAction =
    | { type: typeof REQUEST_ROLL_TO_OPTIONS; payload: RequestRollToOptionsPayload }
    | { type: typeof REQUEST_ROLL_TO_PAYMENTS }
    | { type: typeof REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR; payload: RequestRollToPaymentsPayload }
    | { type: typeof REQUEST_ROLL_TO_PAYMENTS_FAILURE; payload: RequestRollToPaymentsFailurePayload }
    | { type: typeof UPDATE_ROLL_TO_OPTIONS; payload: RequestRollToOptionsPayload }
    | { type: typeof UPDATE_ROLL_TARGET_PAYMENT; payload: number }
    | { type: typeof UPDATE_ROLL_TARGET_BALANCE_DUE; payload: number }
    | { type: typeof UPDATE_ROLL_TARGET_CASH_DUE; payload: number }
    | { type: typeof UPDATE_ROLL_TARGET_PROFIT; payload: number }
    | { type: typeof CLEAR_ROLL_TO_OPTIONS }
    | { type: typeof ROLL_TO_PAYMENT_TOGGLE }
    | { type: typeof SET_UNSAVED_CHANGES; payload: RequestRollToOptionsPayload }
    | { type: typeof UPDATE_ADJUST_ROLL_TO_CHANGES; payload: RollToActionValues | undefined }
    | { type: typeof UPDATE_ROLL_ADJUST; payload: RollToActionValues | undefined; meta?: Record<'actionValueType', string> }
    | { type: typeof REVERT_ROLL_TO_ORIGINAL; meta?: Record<'actionValueType', string> }
    | { type: typeof RESET_ROLL_TO_OPTIONS }
    | { type: typeof APPLY_ROLL_TO_OPTIONS }
    | { type: typeof APPLY_ROLL_TO_OPTIONS_SUCCESS; payload: TargetStatus }
    | { type: typeof APPLY_ROLL_TO_OPTIONS_FAILURE }
    | { type: typeof CLEAR_ROLL_TO_ERRORS }
    | { type: typeof SET_ROLL_TO_ACTIVE_TAB; payload: ROLL_TO_OPTIONS_LABELS; meta?: { middleware?: { rollTo?: boolean } } }
    | { type: typeof PAYMENT_RECEIVED; payload: any; meta?: { originalAction?: any; offerType?: string } }
    | {
          type: typeof REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPQ;
          payload: RequestRollToPaymentUPQPayload;
      };

export type RollToOptionsReducer = Reducer<RollToOptionsState, RollToOptionsAction>;

export const initialState: RollToOptionsState = {
    balanceDue: 0,
    rollToItems: null,
    hasUnsavedChanges: false,
    rollToSlideOutExpanded: false,
    tabStateByLabel: {}
};

const getResolvedActiveTab = (state: RollToOptionsState): ROLL_TO_OPTIONS_LABELS => {
    return state.activeTab || ROLL_TO_OPTIONS_LABELS.PAYMENT;
};

const getTabState = (state: RollToOptionsState, activeTab: ROLL_TO_OPTIONS_LABELS): RollToTabState => {
    return state.tabStateByLabel?.[activeTab] || {};
};

const updateTabState = (
    state: RollToOptionsState,
    activeTab: ROLL_TO_OPTIONS_LABELS,
    updates: Partial<RollToTabState>
): RollToOptionsState => ({
    ...state,
    activeTab,
    tabStateByLabel: {
        ...state.tabStateByLabel,
        [activeTab]: {
            ...getTabState(state, activeTab),
            ...updates
        }
    }
});

/**
 * Safely merges new adjust values with existing ones.
 * Preserves all existing properties when merging partial updates.
 */
const mergeAdjustValues = (
    existingAdjust: RollToActionValues | undefined,
    newAdjust: RollToActionValues | undefined
): RollToActionValues => ({
    ...(existingAdjust || {}),
    ...(newAdjust || {})
});

/**
 * Checks if a tab has a target value set.
 * Used to determine if adjustment fields should be visible.
 */
const hasTabTargetValue = (tabState: RollToTabState, tab: ROLL_TO_OPTIONS_LABELS): boolean => {
    const targetField = ROLL_TO_TARGET_FIELD_MAP[tab as keyof typeof ROLL_TO_TARGET_FIELD_MAP] as keyof RollToTabState;
    return targetField ? tabState[targetField] != null : false;
};

/**
 * Syncs root rollToItems to tab state while preserving tab-specific actionValue and rollTo.
 * This ensures shared calculation data is available while maintaining tab-specific rollTo values.
 */
const syncRootToTabRollToItems = (
    rootRollToItems: RollToActions | null,
    tabRollToItems: RollToActions | null | undefined
): RollToActions | null => {
    if (!rootRollToItems) {
        return tabRollToItems || null;
    }

    return {
        ...rootRollToItems,
        rollTo: tabRollToItems?.rollTo,
        adjust: {
            ...(rootRollToItems.adjust || {}),
            // Preserve tab's target actionValue over root's
            ...(tabRollToItems?.adjust?.actionValue != null && {
                actionValue: tabRollToItems.adjust.actionValue
            })
        }
    };
};

/**
 * Updates both tab state and root state atomically to maintain consistency.
 * Ensures root and tab rollToItems stay synchronized.
 */
const updateBothTabAndRootState = (
    state: RollToOptionsState,
    activeTab: ROLL_TO_OPTIONS_LABELS,
    tabUpdates: Partial<RollToTabState>,
    rootUpdates: Partial<RollToOptionsState>
): RollToOptionsState => {
    const stateWithTabUpdates = updateTabState(state, activeTab, tabUpdates);
    return {
        ...stateWithTabUpdates,
        ...rootUpdates
    };
};

/**
 * Helper to handle target value updates for Payment, Balance Due, and Profit tabs.
 * Reduces duplication by centralizing the common pattern of syncing and updating state.
 */
const updateTargetValue = (
    state: RollToOptionsState,
    activeTab: ROLL_TO_OPTIONS_LABELS,
    targetField: 'targetPayment' | 'targetBalanceDue' | 'targetProfit' | 'targetCashDue',
    payload: number
): RollToOptionsState => {
    const existingTabState = getTabState(state, activeTab);
    const rollToItemsForTab = syncRootToTabRollToItems(state.rollToItems, existingTabState.rollToItems);

    return updateBothTabAndRootState(
        state,
        activeTab,
        {
            [targetField]: payload,
            rollToItems: rollToItemsForTab
        },
        { [targetField]: payload }
    );
};

const inferActionTab = (state: RollToOptionsState, action: RollToOptionsAction): ROLL_TO_OPTIONS_LABELS => {
    switch (action.type) {
        case UPDATE_ROLL_ADJUST:
        case REVERT_ROLL_TO_ORIGINAL:
            return (action.meta?.actionValueType as ROLL_TO_OPTIONS_LABELS) || getResolvedActiveTab(state);
        case REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR:
            if (action.payload.targetPayment != null) {
                return ROLL_TO_OPTIONS_LABELS.PAYMENT;
            }
            if (action.payload.targetBalanceDue != null) {
                return ROLL_TO_OPTIONS_LABELS.BALANCE_DUE;
            }
            if (action.payload.targetCashDue != null) {
                return ROLL_TO_OPTIONS_LABELS.CASH_DUE;
            }
            if (action.payload.targetProfit != null) {
                return ROLL_TO_OPTIONS_LABELS.PROFIT;
            }
            return getResolvedActiveTab(state);
        case REQUEST_ROLL_TO_OPTIONS:
        case UPDATE_ROLL_TO_OPTIONS:
            return (action.payload.rollToItems?.current?.adjustmentType as ROLL_TO_OPTIONS_LABELS) || getResolvedActiveTab(state);
        default:
            return getResolvedActiveTab(state);
    }
};

const applyRollToPayloadToTab = (
    state: RollToOptionsState,
    activeTab: ROLL_TO_OPTIONS_LABELS,
    payload: RequestRollToOptionsPayload
): RollToOptionsState => {
    const existingTabState = getTabState(state, activeTab);
    const existingRollToItems = existingTabState.rollToItems || {};
    const incomingRollToItems = payload.rollToItems || {};
    const nextRollToItems: RollToActions = {
        ...existingRollToItems,
        ...incomingRollToItems,
        adjust:
            incomingRollToItems.current != null
                ? existingRollToItems.adjust || incomingRollToItems.adjust || incomingRollToItems.current
                : incomingRollToItems.adjust != null
                ? {
                      ...existingRollToItems.adjust,
                      ...incomingRollToItems.adjust
                  }
                : existingRollToItems.adjust
    };

    const amountDueUpdates = pick(payload, 'balanceDue', 'cashDue', 'payment');

    const nextState = updateTabState(state, activeTab, {
        rollToItems: nextRollToItems,
        ...amountDueUpdates
    });

    return {
        ...nextState,
        rollToItems: nextRollToItems,
        ...amountDueUpdates
    };
};

export const reducer: RollToOptionsReducer = (state = initialState, action: RollToOptionsAction) => {
    switch (action.type) {
        case CLEAR_ROLL_TO_ERRORS: {
            const activeTab = inferActionTab(state, action as RollToOptionsAction);
            const nextState = updateTabState(state, activeTab, { errors: [] });
            return {
                ...nextState,
                errors: []
            };
        }

        case REQUEST_ROLL_TO_OPTIONS: {
            const activeTab = inferActionTab(state, action);
            return applyRollToPayloadToTab(state, activeTab, action.payload);
        }

        case REQUEST_ROLL_TO_PAYMENTS: {
            return {
                ...state,
                isCalculating: true,
                errors: []
            };
        }

        case REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR: {
            const activeTab = inferActionTab(state, action);
            const { rollProgramItems, messages, targetPayment, targetBalanceDue, targetProfit, targetCashDue } = action.payload;
            const existingTabErrors = getTabState(state, activeTab).errors || [];

            if (!rollProgramItems) {
                const nextState = updateTabState(
                    {
                        ...state,
                        isCalculating: false
                    },
                    activeTab,
                    {
                        errors: [...existingTabErrors],
                        messages
                    }
                );

                return {
                    ...nextState,
                    errors: [...existingTabErrors],
                    messages
                };
            }

            const existingTabRollTo = getTabState(state, activeTab).rollToItems?.rollTo || {};
            const extractedRollTo = extractRollValuesFromProgramItems(rollProgramItems);

            const rollTo = {
                ...existingTabRollTo,
                ...extractedRollTo,
                actionValue: targetPayment ?? targetBalanceDue ?? targetCashDue ?? targetProfit
            };

            const newErrors =
                parseErrorMessages({
                    ...action.payload,
                    rollProgramItems,
                    messages: []
                }) || [];
            const errors = [...existingTabErrors, ...newErrors];

            const existingTabRollToItems = getTabState(state, activeTab).rollToItems;

            // Sync current and adjust from root when calculating (ensures shared values persist)
            const syncedRollToItems: RollToActions = {
                current: state.rollToItems?.current || existingTabRollToItems?.current,
                adjust: mergeAdjustValues(
                    state.rollToItems?.adjust,
                    existingTabRollToItems?.adjust?.actionValue != null
                        ? { actionValue: existingTabRollToItems.adjust.actionValue }
                        : undefined
                ),
                rollTo,
                profit: existingTabRollToItems?.profit
            };

            return updateBothTabAndRootState(
                { ...state, isCalculating: false },
                activeTab,
                {
                    rollToItems: syncedRollToItems,
                    errors,
                    messages
                },
                {
                    rollToItems: {
                        ...state.rollToItems,
                        current: syncedRollToItems.current,
                        adjust: syncedRollToItems.adjust,
                        profit: syncedRollToItems.profit
                    },
                    errors,
                    messages
                }
            );
        }

        case REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPQ: {
            const activeTab = getResolvedActiveTab(state);
            const { actionValue, profit, totalTaxes, balanceDue, cashDue, totalFees, VppAndProtectionTotal, payment } =
                action.payload;
            const existingRollToItems = getTabState(state, activeTab).rollToItems || {};
            const existingTabErrors = getTabState(state, activeTab).errors || [];
            const errors =
                parseErrorMessages({
                    ...action.payload,
                    rollProgramItems: [],
                    messages: []
                }) || [];
            const nextErrors = [...existingTabErrors, ...errors];

            const nextState = updateTabState(
                {
                    ...state,
                    isCalculating: false
                },
                activeTab,
                {
                    balanceDue,
                    cashDue,
                    payment,
                    errors: nextErrors,
                    rollToItems: {
                        ...existingRollToItems,
                        profit: profit ?? existingRollToItems.profit,
                        adjust: {
                            ...existingRollToItems.adjust,
                            actionValue: actionValue ?? existingRollToItems.adjust?.actionValue,
                            taxesAndFees: totalTaxes + totalFees,
                            addOns: VppAndProtectionTotal + totalFees
                        }
                    }
                }
            );

            return {
                ...nextState,
                balanceDue,
                cashDue,
                payment,
                errors: [...(state.errors || []), ...errors],
                rollToItems: {
                    ...state.rollToItems,
                    profit: profit ?? state.rollToItems?.profit,
                    adjust: {
                        ...state.rollToItems?.adjust,
                        actionValue: actionValue ?? state.rollToItems?.adjust?.actionValue,
                        taxesAndFees: totalTaxes + totalFees,
                        addOns: VppAndProtectionTotal + totalFees
                    }
                }
            };
        }

        case REQUEST_ROLL_TO_PAYMENTS_FAILURE: {
            const activeTab = getResolvedActiveTab(state);
            const { response } = action.payload;
            const errors = response?.errors || [];
            const errorMessage = !errors.length ? response?.message || 'An unexpected error occurred' : '';

            if (errorMessage) {
                errors.push({ message: errorMessage });
            }

            const nextState = updateTabState(
                {
                    ...state,
                    isCalculating: false
                },
                activeTab,
                {
                    errors: [...(getTabState(state, activeTab).errors || []), ...errors]
                }
            );

            return {
                ...nextState,
                errors: [...(state.errors || []), ...errors]
            };
        }

        case UPDATE_ROLL_TO_OPTIONS: {
            const activeTab = inferActionTab(state, action);
            return applyRollToPayloadToTab(state, activeTab, action.payload);
        }

        case UPDATE_ADJUST_ROLL_TO_CHANGES: {
            const activeTab = getResolvedActiveTab(state);
            const existingRollToItems = getTabState(state, activeTab).rollToItems || {};
            const mergedAdjust = mergeAdjustValues(existingRollToItems.adjust, action.payload);

            return updateBothTabAndRootState(
                state,
                activeTab,
                {
                    rollToItems: {
                        ...existingRollToItems,
                        adjust: mergedAdjust
                    }
                },
                {
                    rollToItems: {
                        ...state.rollToItems,
                        adjust: mergeAdjustValues(state.rollToItems?.adjust, action.payload)
                    }
                }
            );
        }

        case UPDATE_ROLL_ADJUST: {
            const activeTab = inferActionTab(state, action);
            const existingTabRollToItems = getTabState(state, activeTab).rollToItems || {};
            const mergedAdjust = mergeAdjustValues(existingTabRollToItems.adjust, action.payload);

            const updatedRollToItems: RollToActions = {
                ...existingTabRollToItems,
                adjust: mergedAdjust
            };

            return updateBothTabAndRootState(
                { ...state, activeTab },
                activeTab,
                { rollToItems: updatedRollToItems },
                {
                    rollToItems: {
                        ...state.rollToItems,
                        adjust: mergeAdjustValues(state.rollToItems?.adjust, action.payload)
                    }
                }
            );
        }

        case REVERT_ROLL_TO_ORIGINAL: {
            const activeTab = inferActionTab(state, action);
            return {
                ...state,
                activeTab
            };
        }

        case UPDATE_ROLL_TARGET_PAYMENT: {
            return updateTargetValue(state, ROLL_TO_OPTIONS_LABELS.PAYMENT, 'targetPayment', action.payload);
        }

        case CLEAR_ROLL_TO_OPTIONS: {
            return { ...initialState, rollToSlideOutExpanded: state.rollToSlideOutExpanded };
        }

        case ROLL_TO_PAYMENT_TOGGLE: {
            return {
                ...state,
                rollToSlideOutExpanded: !state.rollToSlideOutExpanded
            };
        }

        case SET_UNSAVED_CHANGES: {
            return {
                ...state,
                hasUnsavedChanges: Boolean(action.payload)
            };
        }

        case APPLY_ROLL_TO_OPTIONS: {
            return {
                ...state,
                isCalculating: true
            };
        }

        case APPLY_ROLL_TO_OPTIONS_SUCCESS: {
            return {
                ...state,
                targetStatus: action.payload,
                isCalculating: false
            };
        }

        case APPLY_ROLL_TO_OPTIONS_FAILURE: {
            return {
                ...state,
                isCalculating: false
            };
        }

        case RESET_ROLL_TO_OPTIONS: {
            return {
                ...initialState,
                activeTab: state.activeTab,
                rollToSlideOutExpanded: state.rollToSlideOutExpanded
            };
        }

        case SET_ROLL_TO_ACTIVE_TAB: {
            const newTab = action.payload;
            const existingTabState = getTabState(state, newTab);

            // Only sync root rollToItems to tab if tab has a target value set
            // This ensures adjustment fields only appear after target is entered
            const rollToItemsForTab = hasTabTargetValue(existingTabState, newTab)
                ? syncRootToTabRollToItems(state.rollToItems, existingTabState.rollToItems)
                : existingTabState.rollToItems || null;

            return updateTabState({ ...state, activeTab: newTab }, newTab, {
                rollToItems: rollToItemsForTab
            });
        }

        case PAYMENT_RECEIVED: {
            // Handle PO response when triggered by tab change
            if (action.meta?.originalAction?.type === SET_ROLL_TO_ACTIVE_TAB) {
                const activeTab = getResolvedActiveTab(state);
                const offerType = action.meta.offerType;

                if (!offerType || !activeTab) {
                    return {
                        ...state,
                        isCalculating: false
                    };
                }

                const paymentData = action.payload[offerType];

                if (!paymentData) {
                    return {
                        ...state,
                        isCalculating: false
                    };
                }

                // Update tab state with new payment/balance due values from PO response
                return updateTabState(
                    {
                        ...state,
                        isCalculating: false
                    },
                    activeTab,
                    {
                        payment: paymentData.payment || state.payment,
                        balanceDue: paymentData.dueAtSigning?.totalDueAtSigning || state.balanceDue
                    }
                );
            }
            return state;
        }

        case UPDATE_ROLL_TARGET_BALANCE_DUE: {
            return updateTargetValue(state, ROLL_TO_OPTIONS_LABELS.BALANCE_DUE, 'targetBalanceDue', action.payload);
        }

        case UPDATE_ROLL_TARGET_CASH_DUE: {
            return updateTargetValue(state, ROLL_TO_OPTIONS_LABELS.CASH_DUE, 'targetCashDue', action.payload);
        }

        case UPDATE_ROLL_TARGET_PROFIT: {
            return updateTargetValue(state, ROLL_TO_OPTIONS_LABELS.PROFIT, 'targetProfit', action.payload);
        }

        default: {
            return state;
        }
    }
};

function extractRollValuesFromProgramItems(rollProgramItems: RollProgramItem[]) {
    const rollValuesToMap: RollValueToMap = {
        SellingPrice: 'sellingPrice',
        DownPayment: 'downPayment',
        Trade: 'trade',
        Rate: 'rate',
        Term: 'term',
        BalloonAmount: 'balloonAmount'
    };

    const tradeItems: Array<{ value: number; callerItemId: number }> = [];

    const result = rollProgramItems.reduce((acc: RollToActionValues, item: RollProgramItem) => {
        const key = rollValuesToMap[item.rollValueType];

        if (item.tradeCallerItemId) {
            tradeItems.push({
                value: item.rollValue,
                callerItemId: item.tradeCallerItemId
            });
            return acc;
        }

        if (key && key !== 'adjustmentType' && key !== 'tradeIns') {
            acc[key] = item.rollValue;
        }

        return acc;
    }, {} as RollToActionValues);

    if (tradeItems.length === 1) {
        result.trade = tradeItems[0].value;
    }
    result.tradeIns = tradeItems.map((item) => ({ ...item, adjusted: false }));

    return result;
}
